import { useState } from "react";
import { motion } from "framer-motion";
import { Slider } from "../ui/slider";
import { useDeck } from "../../lib/deck-context";
import { fmtM } from "../../lib/model";
import { Card, Eyebrow, Note, Reveal, Section, SectionHeader, StatusBadge } from "../deck/primitives";

const SOURCES = [
  { t: "Construction lender", tier: "Senior debt" },
  { t: "Joint Venture equity", tier: "Equity" },
  { t: "Sponsor economics", tier: "Equity" },
  { t: "Tenant / operator capital", tier: "Tenant build-out" },
  { t: "Public infrastructure participation", tier: "Where legally available" },
  { t: "Sponsorship / naming / programming revenue", tier: "Where appropriate" },
];

export default function Capital() {
  const { m, inputs, setInput } = useDeck();
  const [hover, setHover] = useState(null);
  const layers = [
    { k: "equity", label: "Working Equity", pct: inputs.equityPct, amt: m.equity, cls: "bg-arq", text: "text-white" },
    { k: "debt", label: "Working Debt · Senior Construction", pct: inputs.debtPct, amt: m.debt, cls: "bg-ink", text: "text-white" },
  ];
  return (
    <Section testid="section-capital">
      <SectionHeader num="09" eyebrow="Capital Stack" title="How the project is capitalized." lede="Illustrative 65 / 35 debt-to-equity against the current preliminary Total Development Cost. Land acquisition cost: $0 assumed." status="nyc" />
      <div className="grid lg:grid-cols-12 gap-6">
        <Reveal className="lg:col-span-5">
          <Card className="p-8 h-full">
            <div className="flex items-center justify-between">
              <Eyebrow>Total Development Cost</Eyebrow>
              <span data-testid="capital-tdc" className="font-display text-3xl text-ink tabular-nums">{fmtM(inputs.tdc)}</span>
            </div>
            <div data-testid="capital-stack-chart" className="mt-8 h-[380px] flex flex-col gap-1.5 rounded-xl overflow-hidden">
              {layers.map((l) => (
                <motion.div
                  key={l.k}
                  data-testid={`capital-layer-${l.k}`}
                  onMouseEnter={() => setHover(l.k)}
                  onMouseLeave={() => setHover(null)}
                  className={`${l.cls} ${l.text} relative px-7 flex items-center justify-between cursor-default transition-opacity duration-200`}
                  style={{ opacity: hover && hover !== l.k ? 0.55 : 1 }}
                  animate={{ height: `${l.pct}%` }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.2em] text-white/70 uppercase">{l.label}</div>
                    <div className="mt-1 text-sm text-white/70">Target · not committed</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-4xl tabular-nums">{l.pct}%</div>
                    <div className="text-sm tabular-nums text-white/85">≈ {fmtM(l.amt)}</div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-7">
              <div className="flex justify-between text-sm mb-3"><span className="text-steel">Construction debt</span><span data-testid="capital-debt-pct" className="tabular-nums text-ink">{inputs.debtPct}%</span></div>
              <Slider data-testid="capital-debt-slider" min={40} max={80} step={1} value={[inputs.debtPct]} onValueChange={([v]) => setInput("debtPct", v)} />
            </div>
          </Card>
        </Reveal>
        <div className="lg:col-span-7 space-y-6">
          <Reveal delay={0.1}>
            <div data-testid="capital-not-committed-label" className="rounded-[14px] border border-dashed border-ink/30 bg-white px-7 py-5 flex flex-wrap items-center gap-4">
              <StatusBadge s="nyc" />
              <span className="font-display text-xl text-ink">Working Assumptions — Not Committed Financing</span>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { s: "verified", t: "Committed", v: "$0", d: "No lender, equity or tenant capital is under contract today." },
                { s: "target", t: "Target", v: `${fmtM(m.debt)} + ${fmtM(m.equity)}`, d: `${inputs.debtPct}% debt / ${inputs.equityPct}% equity working structure.` },
                { s: "nyc", t: "Potential", v: "6 sources", d: "Identified pathways — none committed." },
              ].map((c) => (
                <Card key={c.t} data-testid={`capital-tier-${c.t.toLowerCase()}`} className="p-6">
                  <div className="font-mono text-[10px] tracking-[0.2em] text-steel uppercase">{c.t}</div>
                  <div className="mt-3 font-display text-2xl text-ink tabular-nums">{c.v}</div>
                  <div className="mt-3 text-xs text-steel leading-relaxed">{c.d}</div>
                </Card>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <Card className="divide-y divide-line">
              <div className="px-7 py-5"><Eyebrow>Potential Sources</Eyebrow></div>
              {SOURCES.map((s) => (
                <div key={s.t} data-testid={`capital-source-${s.t.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="px-7 py-4 flex items-center justify-between gap-4">
                  <div><div className="text-[15px] text-ink">{s.t}</div><div className="text-xs text-steel">{s.tier}</div></div>
                  <StatusBadge s="nyc" />
                </div>
              ))}
            </Card>
          </Reveal>
        </div>
      </div>
      <Note className="mt-6">Capital stack is illustrative. Final lender leverage, pricing and guarantees remain to be determined. Final ground rent and public-improvement responsibilities are still to be negotiated with the City.</Note>
    </Section>
  );
}
